/* eslint-disable max-len */
import React from "react";
import type { Section } from "../../content/schema-types";
import { Button, ResponsiveImage, RichText } from "../ui";
import { ContactRequestModal } from "../contactRequest/ContactRequestModal";

export type ContactSectionProps = Extract<Section, { type: "contact" }>["content"] & {
    /**
     * Traegt diese Sektion den `#kontakt`-Anker?
     *
     * Hintergrund (Abnahme 1.8.2026): `ContactSection` UND `ClosingCtaSection`
     * setzten ihn beide fest verdrahtet. Auf 48 gebauten Seiten stand er dadurch
     * doppelt — HTML-invalid und fuer Screenreader-Navigation unsauber.
     *
     * Standard ist `true`, und das ist die wichtigere Haelfte der Loesung: auf 46
     * Seiten ist diese Sektion die EINZIGE mit dem Anker. Wer ihn ihr pauschal
     * naehme, liesse dort jeden `#kontakt`-Link ins Leere laufen.
     *
     * Nur die Seiten, die zusaetzlich eine `ClosingCtaSection` rendern, setzen
     * `anchor={false}` — dort springt der Link weiterhin auf den Handlungsaufruf,
     * genau wie bisher. Das Sprungziel aendert sich also auf keiner Seite.
     */
    anchor?: boolean;
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion? (12.8.2026)
     *
     * Im Kapitel «Fangen wir an» der Startseite steht der Abschluss-Aufruf
     * bereits als `h2`; «Lieber erst einmal fragen?» faellt auf `h3`. Flaeche
     * und Aussenabstand setzt das Kapitel — der Farbverlauf laeuft dann ueber
     * beide Teile statt nur ueber diesen.
     *
     * ⚠️ Die SCHRIFTFARBE bleibt hier: `variant="dark"` faerbt sie weiss, und
     * ohne sie stuende weisse Schrift auf hellem Grund, sobald jemand das
     * Kapitel umfaerbt. Das Gefaess faerbt die Flaeche, der Teil seinen Text.
     *
     * Der `#kontakt`-Anker bleibt an DIESER Sektion, nicht am Kapitel: Er soll
     * bei einem Menschen landen, nicht bei zwei bezahlten Angeboten (v0.3 F5).
     */
    nested?: boolean;
};

type SizeConfig = {
    sectionPadding: string;
    containerMaxWidth: string;
    titleSize: string;
    titleMargin: string;
    imageSize: string;
    imageBorder: string;
    imageWidths: number[];
    imageSizes: string;
    nameSize: string;
    titleTextSize: string;
    descriptionSize: string;
    buttonSize: "sm" | "lg";
    buttonGap: string;
    buttonContainerClass: string;
    layoutClass: string;
    personInfoClass: string;
    personInfoLayout: string;
};

const sizeConfigs: Record<"default" | "large", SizeConfig> = {
    large: {
        sectionPadding: "py-16 md:py-20",
        containerMaxWidth: "max-w-5xl",
        titleSize: "text-3xl md:text-4xl",
        titleMargin: "mb-6",
        imageSize: "w-64 h-64 md:w-72 md:h-72",
        imageBorder: "border-4",
        imageWidths: [320],
        imageSizes: "(max-width: 768px) 256px, 288px",
        nameSize: "text-xl md:text-2xl",
        titleTextSize: "text-base",
        descriptionSize: "text-lg",
        buttonSize: "lg",
        buttonGap: "gap-4",
        buttonContainerClass: "flex flex-col gap-4",
        layoutClass: "grid grid-cols-1 lg:grid-cols-2 gap-12 items-center",
        personInfoClass: "space-y-6",
        personInfoLayout: "flex justify-center lg:justify-start",
    },
    default: {
        sectionPadding: "py-12",
        // Wie `large` begrenzt (7.8.2026, Entscheid Jan): Das Feld war leer, die
        // Sektion lief also ueber die volle Fensterbreite — auf einem breiten
        // Monitor stand der Text ganz links, die Buttons ganz rechts, dazwischen
        // rund 900px Leerraum. Fiel bis heute niemandem auf, weil alle 27
        // Kontakt-Sektionen im Repo `large` nutzten und `default` nie im
        // Einsatz war.
        containerMaxWidth: "max-w-5xl",
        titleSize: "text-2xl",
        titleMargin: "mb-2",
        imageSize: "w-20 h-20",
        imageBorder: "border-2",
        imageWidths: [320],
        imageSizes: "80px",
        nameSize: "text-base",
        titleTextSize: "text-sm",
        descriptionSize: "text-sm",
        buttonSize: "sm",
        buttonGap: "gap-3",
        buttonContainerClass: "flex flex-col gap-3",
        layoutClass: "flex flex-col md:flex-row items-center gap-8 md:gap-12",
        personInfoClass: "",
        personInfoLayout: "flex items-center gap-6 flex-1",
    },
};

export const ContactSection: React.FC<ContactSectionProps> = ({
    title,
    description,
    person,
    cta,
    variant = "dark",
    size = "default",
    tagline,
    /*
     * «Termin vereinbaren» ist der primaere Handlungsaufruf (Jan, 3.8.2026).
     *
     * Stand bisher auf `secondary` — und keine Aufrufstelle ueberschreibt das,
     * die Voreinstellung galt also auf allen 136 Seiten mit Kontakt-Sektion.
     * Genau deshalb gehoert die Aenderung hierher und nicht an die Startseite:
     * der Terminknopf ist ueberall derselbe Abschluss.
     *
     * Auf dunklem Grund (`variant="dark"`, der Normalfall) rendert `Button`
     * `primary` als helle Flaeche — der Knopf gewinnt Gewicht, ohne dass die
     * Sektion umgebaut werden muesste.
     */
    ctaVariant = "primary",
    anchor = true,
    nested = false,
    topic,
    calendarUrl,
    calendarId,
}) => {
    // Vor dem `return null`, sonst laufen bei personloser Sektion andere Hooks
    // als bei einer mit Person — genau der Fall, den die Hook-Regeln verbieten.
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    if (!person) {
        return null;
    }

    // TODO: these text colors should not be hardcoded
    // Im Kapitel faerbt das Gefaess die Flaeche — hier bleibt nur die Schrift,
    // sonst stuende sie weiss auf hellem Grund, sobald jemand das Kapitel
    // umfaerbt.
    const bgClass = nested
        ? variant === "dark"
            ? "text-white"
            : ""
        : variant === "dark"
          ? "bg-gradient text-white"
          : "bg-gray-50";
    const textColorClass = variant === "dark" ? "text-white" : "text-primary-dark";
    const subTextClass = variant === "dark" ? "text-white/80" : "text-gray-600";
    const borderClass = variant === "dark" ? "border-white" : "border-primary-dark";

    const config = sizeConfigs[size];
    const isLarge = size === "large";
    const Heading = nested ? "h3" : "h2";

    /**
     * Der Terminlink wird zum Modal-Ausloeser (Endgame v0.3 § 1).
     *
     * Die Regel richtet sich nach der ABSICHT, nicht nach der Anwesenheit eines
     * `href`: ein Link auf den geteilten Google-Kalender heisst «Termin
     * vereinbaren» — und genau das tut das Modal jetzt besser, ohne dass der
     * Besuch die Seite verlaesst. Solche Links uebernimmt es deshalb.
     *
     * Anders herum waere es eine Attrappe: `cta.href` steht in 74 Content-
     * Dateien noch auf dem Kalender. Haette der href Vorrang, bliebe das Modal
     * auf ALLEN 102 gebauten Seiten unerreichbar — bei gruenem Gate. Genau das
     * war beim ersten Bauen der Fall, und keine der 2214 Unit-Tests sah es;
     * erst die Inspektion des Artefakts zeigte 0 von 102 Knoepfen.
     *
     * Zwei Aufrufstellen (gross/klein), deshalb hier einmal definiert.
     */
    const isCalendarBooking = (href?: string) => Boolean(href?.includes("calendar.app.google"));

    const renderCta = (className?: string) => {
        if (!cta?.text) {
            return null;
        }
        const shared = {
            variant: ctaVariant,
            size: config.buttonSize,
            onDark: variant === "dark",
            ...(className ? { className } : {}),
        };

        // Ein eigener `calendarUrl` ist der ausdrueckliche Wunsch nach dem
        // externen Kalender — der schlaegt alles andere.
        if (calendarUrl) {
            return (
                <Button href={calendarUrl} {...shared}>
                    {cta.text}
                </Button>
            );
        }

        // Echte Navigation (z. B. auf eine Angebotsseite) bleibt Navigation.
        if (cta.href && !isCalendarBooking(cta.href)) {
            return (
                <Button href={cta.href} {...shared}>
                    {cta.text}
                </Button>
            );
        }

        return (
            <Button onClick={() => setIsModalOpen(true)} {...shared}>
                {cta.text}
            </Button>
        );
    };

    return (
        <section
            id={anchor ? "kontakt" : undefined}
            className={nested ? `scroll-mt-24 ${bgClass}` : `${config.sectionPadding} ${bgClass}`}
        >
            {/*
                Ohne `container` (Jan, 5.8.2026): die Klasse rastete auf
                Breakpoint-Breiten, ein `max-w-*` hob das wieder auf — je nach
                Fenster stand die Sektion mit 16px oder mit 300px vom Rand.
                Feste Breite plus Padding waechst dagegen monoton mit.
            */}
            <div className={`mx-auto w-full px-4 md:px-12 ${config.containerMaxWidth}`}>
                {isLarge ? (
                    <div className={config.layoutClass}>
                        {/* Image on left. Faellt weg, wenn kein Portrait vorliegt
                            (7.8.2026) — lieber kein Bild als das eines Kollegen. */}
                        {person.image && (
                            <div className={config.personInfoLayout}>
                                <div
                                    className={`${config.imageSize} rounded-full overflow-hidden flex-shrink-0 ${config.imageBorder} ${borderClass}`}
                                >
                                    <ResponsiveImage
                                        src={person.image}
                                        alt={person.name}
                                        className="w-full h-full"
                                        widths={config.imageWidths}
                                        sizes={config.imageSizes}
                                        objectFit="cover"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Content on right */}
                        <div className={config.personInfoClass}>
                            <Heading
                                className={`${config.titleSize} font-bold ${config.titleMargin} ${textColorClass}`}
                            >
                                {title}
                            </Heading>
                            {/* Die Funktionsbezeichnung (`person.title`) wird seit dem
                                12.8.2026 NICHT mehr angezeigt (Jan) — der Name genuegt,
                                die Rolle stand als zusaetzliche Zeile zwischen Namen und
                                Angebot. Das Feld bleibt im Schema und im Content: Es
                                traegt weiterhin die Zuordnung, wer der Ansprechpartner
                                ist, und ein Entfernen waere eine Contentaenderung.

                                ⚠️ Der Name steht seither IM Fliesstext, nicht darueber.
                                Das `<div>` davor war die Klammer um Name UND Rolle; ohne
                                die Rolle blieb ein Container mit einem einzigen fetten
                                Absatz — der Name stand als dritte Ebene zwischen
                                Ueberschrift und Text und wirkte laut Abnahme «frei im
                                Raum» (Jan, 12.8.2026). Als Auftakt des Absatzes gehoert
                                er zu dem, was er sagt. */}
                            <p className={`${config.descriptionSize} ${subTextClass} leading-relaxed`}>
                                <span className={`${config.nameSize} font-semibold ${textColorClass}`}>
                                    {person.name}
                                </span>
                                {" — "}
                                <RichText content={description} />
                            </p>
                            <div className={config.buttonContainerClass}>
                                {renderCta("w-full")}
                                <Button
                                    href={`mailto:${person.email}`}
                                    variant="secondary"
                                    size={config.buttonSize}
                                    className="w-full"
                                    onDark={variant === "dark"}
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                            />
                                        </svg>
                                        {person.email}
                                    </span>
                                </Button>
                                {person.phone && (
                                    <Button
                                        href={`tel:${person.phone.replace(/\s/g, "")}`}
                                        variant="secondary"
                                        size={config.buttonSize}
                                        className="w-full"
                                        onDark={variant === "dark"}
                                    >
                                        <span className="inline-flex items-center gap-2">
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                                />
                                            </svg>
                                            {person.phone}
                                        </span>
                                    </Button>
                                )}
                                {tagline && (
                                    <div className={`text-sm md:text-base ${subTextClass} mt-2`}>{tagline}</div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={config.layoutClass}>
                        <div className={config.personInfoLayout}>
                            {/* Nur der Bildkreis ist bedingt, nicht der Wrapper:
                                der Info-Block daneben ist sein Geschwister. */}
                            {person.image && (
                                <div
                                    className={`${config.imageSize} rounded-full overflow-hidden flex-shrink-0 ${config.imageBorder} ${borderClass}`}
                                >
                                    <ResponsiveImage
                                        src={person.image}
                                        alt={person.name}
                                        className="w-full h-full"
                                        widths={config.imageWidths}
                                        sizes={config.imageSizes}
                                        objectFit="cover"
                                    />
                                </div>
                            )}
                            <div className={config.personInfoClass || "flex-1"}>
                                <Heading
                                    className={`${config.titleSize} font-bold ${config.titleMargin} ${textColorClass}`}
                                >
                                    {title}
                                </Heading>
                                {/* Funktionsbezeichnung entfernt, Name im Fliesstext —
                                    siehe die ausfuehrliche Begruendung oben. */}
                                <p className={`${config.descriptionSize} ${subTextClass} leading-relaxed`}>
                                    <span
                                        className={`${config.nameSize} font-bold ${textColorClass} ${!isLarge ? "font-semibold" : ""}`}
                                    >
                                        {person.name}
                                    </span>
                                    {" — "}
                                    <RichText content={description} />
                                </p>
                            </div>
                        </div>
                        {/* Alle Knoepfe `w-full` in einer Spalte: Vorher stand der
                            E-Mail-Knopf in einer inneren `flex`-Zeile und der Telefon-
                            Knopf darunter allein — jeder so breit wie sein eigener Text.
                            Auf /jobs ergab das 149px (hr@quatico.com) neben 161px
                            (+41 43 243 88 00), untereinander und sichtbar ungleich.
                            Der `large`-Zweig macht es seit jeher so (Z. 226 ff.). */}
                        <div className={config.buttonContainerClass}>
                            {renderCta("w-full")}

                            <Button
                                href={`mailto:${person.email}`}
                                variant="secondary"
                                size={config.buttonSize}
                                className="w-full"
                                onDark={variant === "dark"}
                            >
                                {person.email}
                            </Button>
                            {person.phone && (
                                <Button
                                    href={`tel:${person.phone.replace(/\s/g, "")}`}
                                    variant="secondary"
                                    size={config.buttonSize}
                                    className="w-full"
                                    onDark={variant === "dark"}
                                >
                                    {person.phone}
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/*
                Erst mounten, wenn geoeffnet. Sonst laeuft auf jeder der 46
                Seiten mit dieser Sektion `useContent` mit, obwohl kein Modal
                sichtbar ist — und jede Sektion braeuchte einen
                LanguageProvider, auch dort wo sie ohne auskam.
            */}
            {isModalOpen && (
                <ContactRequestModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    topic={topic ?? title ?? "Allgemeine Anfrage"}
                    recipientEmail={person.email}
                    recipientName={person.name}
                    /*
                     * Der Kalender ergibt sich aus dem ANSPRECHPARTNER, nicht
                     * aus einer globalen Voreinstellung.
                     *
                     * Vorher fielen 88 von 90 Seiten auf `GOOGLE_CALENDAR_ID`
                     * zurueck — darunter 36 mit Michael und 2 mit Ralph als
                     * sichtbarem Ansprechpartner. Ein Besucher haette dort
                     * Michael gesehen, einen Termin gebucht, und der Termin
                     * waere in einem fremden Kalender gelandet; Michael haette
                     * nichts davon im Kalender stehen. Das ist keine
                     * Konfigurationsluecke, sondern eine falsche Buchung.
                     *
                     * `calendarId` im Content bleibt als Ausweg fuer Faelle, in
                     * denen Termin-Kalender und Mailadresse auseinanderfallen.
                     */
                    calendarId={calendarId ?? person.email}
                />
            )}
        </section>
    );
};
